import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Sparkles, X, Send, Loader2, Minus, MessageSquarePlus, Brain, Bookmark, Archive, Copy, Download, Trash2, ArrowLeft, FileText } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const MIN_W = 340, MAX_W = 720, DEFAULT_W = 420;

const MD = ({ text }) => (
  <div className="si-md text-[13px] leading-relaxed">
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
  </div>
);

const ACTIONS = {
  school: [
    { key: "board_report", label: "Board Report", msg: "Generate a board report" },
    { key: "action_plan", label: "Action Plan", msg: "Create a prioritized action plan" },
    { key: "renewal_readiness", label: "Renewal Readiness", msg: "Assess our renewal readiness" },
  ],
  portfolio: [
    { key: "benchmark", label: "Benchmark", msg: "Benchmark my schools" },
    { key: "board_report", label: "Board Report", msg: "Generate a portfolio board report" },
    { key: "action_plan", label: "Action Plan", msg: "Create a prioritized portfolio action plan" },
  ],
};

// authFetch(url, opts) supplied by host (cookie vs header auth). getContext() → object.
export default function IntelligencePanel({ authFetch, getContext, storageKey = "school" }) {
  const [access, setAccess] = useState(null);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat");        // 'chat' | 'saved'
  const [saved, setSaved] = useState([]);
  const [width, setWidth] = useState(() => Number(localStorage.getItem("si_width")) || DEFAULT_W);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(localStorage.getItem(`si_session_${storageKey}`) || "");
  const scrollRef = useRef(null);
  const draggingRef = useRef(false);
  const extraCtxRef = useRef(null);

  useEffect(() => {
    authFetch(`${API}/api/intelligence/access`).then((r) => (r.ok ? r.json() : null))
      .then(setAccess).catch(() => setAccess(null));
  }, []); // eslint-disable-line

  const loadHistory = useCallback(() => {
    if (!sessionRef.current) return;
    authFetch(`${API}/api/intelligence/history?session_id=${sessionRef.current}`)
      .then((r) => (r.ok ? r.json() : { messages: [] }))
      .then((d) => setMessages(d.messages || [])).catch(() => {});
  }, [authFetch]);

  const loadSaved = useCallback(() => {
    authFetch(`${API}/api/intelligence/deliverables`).then((r) => (r.ok ? r.json() : { deliverables: [] }))
      .then((d) => setSaved(d.deliverables || [])).catch(() => {});
  }, [authFetch]);

  useEffect(() => { if (open) loadHistory(); }, [open, loadHistory]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, streaming]);

  // resize handle
  useEffect(() => {
    const move = (e) => {
      if (!draggingRef.current) return;
      const w = Math.min(MAX_W, Math.max(MIN_W, window.innerWidth - e.clientX));
      setWidth(w);
    };
    const up = () => { if (draggingRef.current) { draggingRef.current = false; localStorage.setItem("si_width", String(width)); } };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  }, [width]);

  const send = async (text, opts = {}) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setView("chat");
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    const ctx = { ...(getContext ? getContext() : {}), ...(opts.extraContext || {}) };
    try {
      const r = await authFetch(`${API}/api/intelligence/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current || null, message: msg, client_context: ctx, action: opts.action || null }),
      });
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          const line = p.trim(); if (!line.startsWith("data:")) continue;
          let d; try { d = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (d.delta) setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + d.delta }; return c; });
          if (d.session_id) { sessionRef.current = d.session_id; localStorage.setItem(`si_session_${storageKey}`, d.session_id); }
          if (d.error) setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "⚠️ " + d.error }; return c; });
        }
      }
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "⚠️ Sorry, something went wrong." }; return c; });
    }
    setStreaming(false);
  };

  // "✨ Explain this" bridge — any page can dispatch window event to ask in-context.
  useEffect(() => {
    const handler = (e) => {
      const { question, context } = e.detail || {};
      if (!question) return;
      setOpen(true);
      extraCtxRef.current = context || null;
      setTimeout(() => send(question, { extraContext: context }), 60);
    };
    window.addEventListener("intel-ask", handler);
    return () => window.removeEventListener("intel-ask", handler);
  }, [streaming]); // eslint-disable-line

  const saveDeliverable = async (content) => {
    const title = window.prompt("Save as… (title)", `${access?.context_kind === "portfolio" ? "Portfolio" : "School"} report`);
    if (!title) return;
    try {
      const r = await authFetch(`${API}/api/intelligence/deliverables`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, kind: "report", content, session_id: sessionRef.current || null }),
      });
      if (r.ok) { loadSaved(); }
    } catch {} // eslint-disable-line
  };

  const openSaved = async (id) => {
    const r = await authFetch(`${API}/api/intelligence/deliverables/${id}`);
    if (!r.ok) return;
    const d = await r.json();
    setView("chat");
    setMessages([{ role: "assistant", content: `**${d.title}**\n\n${d.content}` }]);
  };

  const downloadMd = (title, content) => {
    const blob = new Blob([`# ${title}\n\n${content}`], { type: "text/markdown" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `${title.replace(/[^a-z0-9]+/gi, "_")}.md`; a.click(); URL.revokeObjectURL(a.href);
  };

  const deleteSaved = async (id) => {
    await authFetch(`${API}/api/intelligence/deliverables/${id}`, { method: "DELETE" });
    loadSaved();
  };

  const newChat = () => { sessionRef.current = ""; localStorage.removeItem(`si_session_${storageKey}`); setMessages([]); setView("chat"); };

  if (!access || !access.has_access) return null;

  // launcher (when closed)
  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="intel-launcher" aria-label="School Intelligence"
        className="fixed right-5 bottom-5 z-40 flex items-center gap-2 rounded-full bg-[#007a9e] hover:bg-[#00607d] text-white shadow-lg px-4 py-3 transition-transform hover:scale-105 max-[1280px]:px-0 max-[1280px]:h-[52px] max-[1280px]:w-[52px] max-[1280px]:justify-center">
        <Brain className="h-5 w-5" /> <span className="text-sm font-semibold max-[1280px]:sr-only">School Intelligence</span>
      </button>
    );
  }

  return (
    <div className="fixed top-0 right-0 h-screen z-40 flex" data-testid="intel-panel" style={{ width }}>
      <style>{`
        .si-md h1,.si-md h2,.si-md h3{font-weight:700;color:#0b1220;margin:.6em 0 .3em;line-height:1.25}
        .si-md h1{font-size:15px}.si-md h2{font-size:14px}.si-md h3{font-size:13px}
        .si-md p{margin:.4em 0}
        .si-md ul,.si-md ol{margin:.3em 0 .3em 1.1em;padding:0}
        .si-md li{margin:.15em 0}
        .si-md strong{font-weight:700;color:#0b1220}
        .si-md table{border-collapse:collapse;width:100%;margin:.5em 0;font-size:12px}
        .si-md th,.si-md td{border:1px solid #e2e8f0;padding:4px 6px;text-align:left}
        .si-md th{background:#f1f5f9;font-weight:600}
        .si-md code{background:#e2e8f0;padding:0 3px;border-radius:3px;font-size:12px}
        .si-md a{color:#4f46e5;text-decoration:underline}
      `}</style>      <div onMouseDown={() => { draggingRef.current = true; }} data-testid="intel-resize"
        className="w-1.5 cursor-col-resize bg-transparent hover:bg-indigo-300/50" />
      <div className="flex-1 h-full bg-white border-l border-slate-200 shadow-2xl flex flex-col">
        {/* header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100" style={{ background: "#0b1220" }}>
          <div className="flex items-center gap-2 text-white">
            <Brain className="h-4 w-4 text-indigo-300" />
            <span className="text-sm font-bold">School Intelligence</span>
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-500/30 text-indigo-200">AI</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => { setView(view === "saved" ? "chat" : "saved"); if (view !== "saved") loadSaved(); }} title="Saved reports" data-testid="intel-saved-toggle" className="p-1.5 rounded hover:bg-white/10 text-slate-300"><Archive className="h-4 w-4" /></button>
            <button onClick={newChat} title="New conversation" data-testid="intel-new-chat" className="p-1.5 rounded hover:bg-white/10 text-slate-300"><MessageSquarePlus className="h-4 w-4" /></button>
            <button onClick={() => setOpen(false)} title="Minimize" data-testid="intel-minimize" className="p-1.5 rounded hover:bg-white/10 text-slate-300"><Minus className="h-4 w-4" /></button>
            <button onClick={() => setOpen(false)} title="Close" data-testid="intel-close" className="p-1.5 rounded hover:bg-white/10 text-slate-300"><X className="h-4 w-4" /></button>
          </div>
        </div>

        {view === "saved" ? (
          <div className="flex-1 overflow-y-auto px-4 py-4" data-testid="intel-saved-view">
            <button onClick={() => setView("chat")} className="flex items-center gap-1 text-xs text-slate-500 mb-3 hover:text-slate-800"><ArrowLeft className="h-3.5 w-3.5" /> Back to chat</button>
            {saved.length === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center">No saved reports yet. Generate a Board Report or Action Plan and tap Save.</div>
            ) : saved.map((d) => (
              <div key={d.id} className="rounded-lg border border-slate-200 px-3 py-2.5 mb-2" data-testid={`intel-saved-${d.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <button onClick={() => openSaved(d.id)} className="flex items-center gap-2 min-w-0 text-left">
                    <FileText className="h-4 w-4 text-indigo-500 shrink-0" />
                    <span className="text-sm font-medium text-slate-800 truncate">{d.title}</span>
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={async () => { const r = await authFetch(`${API}/api/intelligence/deliverables/${d.id}`); const dd = await r.json(); downloadMd(dd.title, dd.content); }} title="Download" className="p-1 rounded hover:bg-slate-100 text-slate-500"><Download className="h-3.5 w-3.5" /></button>
                    <button onClick={() => deleteSaved(d.id)} title="Delete" className="p-1 rounded hover:bg-red-50 text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
        /* messages */
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="intel-messages">
          {messages.length === 0 && (
            <div className="text-center pt-6">
              <div className="mx-auto h-12 w-12 rounded-2xl bg-indigo-50 flex items-center justify-center mb-3"><Sparkles className="h-6 w-6 text-indigo-600" /></div>
              <p className="text-sm font-semibold text-slate-800">Ask about your {access.context_kind === "portfolio" ? "portfolio" : "school"}</p>
              <p className="text-xs text-slate-400 mt-1 mb-4">I already know your scores, pillars and context.</p>
              <div className="flex flex-wrap justify-center gap-1.5 mb-4" data-testid="intel-actions">
                {(ACTIONS[access.context_kind] || ACTIONS.school).map((a) => (
                  <button key={a.key} onClick={() => send(a.msg, { action: a.key })} data-testid={`intel-action-${a.key}`}
                    className="text-[12px] font-semibold text-indigo-700 rounded-full border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 transition-colors">✨ {a.label}</button>
                ))}
              </div>
              <div className="flex flex-col gap-1.5" data-testid="intel-suggestions">
                {(access.suggested_prompts || []).slice(0, 6).map((s, i) => (
                  <button key={i} onClick={() => send(s)} data-testid={`intel-suggestion-${i}`}
                    className="text-left text-[13px] text-slate-700 rounded-lg border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50 px-3 py-2 transition-colors">{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : "flex flex-col items-start"}>
              <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${m.role === "user" ? "bg-indigo-600 text-white self-end whitespace-pre-wrap" : "bg-slate-100 text-slate-800"}`}
                data-testid={m.role === "user" ? "intel-user-msg" : "intel-ai-msg"}>
                {m.role === "assistant"
                  ? (m.content ? <MD text={m.content} /> : (streaming && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : ""))
                  : m.content}
              </div>
              {m.role === "assistant" && m.content && !(streaming && i === messages.length - 1) && (
                <div className="flex items-center gap-2 mt-1 ml-1">
                  <button onClick={() => saveDeliverable(m.content)} data-testid={`intel-save-${i}`} className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-indigo-600"><Bookmark className="h-3 w-3" /> Save</button>
                  <button onClick={() => navigator.clipboard.writeText(m.content)} className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-indigo-600"><Copy className="h-3 w-3" /> Copy</button>
                </div>
              )}
            </div>
          ))}
        </div>
        )}

        {/* input */}
        <div className="border-t border-slate-100 p-3">
          <div className="flex items-end gap-2 rounded-xl border border-slate-200 focus-within:border-indigo-400 px-3 py-2">
            <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1} placeholder="Ask School Intelligence…"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              className="flex-1 resize-none outline-none text-[13px] max-h-28 bg-transparent" data-testid="intel-input" />
            <button onClick={() => send()} disabled={streaming || !input.trim()} data-testid="intel-send"
              className="p-2 rounded-lg bg-indigo-600 text-white disabled:opacity-40 hover:bg-indigo-700">
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-[10px] text-slate-400 mt-1.5 text-center">Grounded only in your permitted data. Verify important decisions.</p>
        </div>
      </div>
    </div>
  );
}
